import { useParams, useNavigate } from 'react-router-dom'
import { Plus, RefreshCw, Search, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTickets } from '../hooks/useTickets'
import { useProjects } from '../hooks/useProjects'
import { TicketCard } from '../components/TicketCard'
import { KanbanColumn } from '../components/KanbanColumn'
import { ViewToggle, type ViewMode } from '../components/ViewToggle'
import { EmptyState } from '../components/EmptyState'
import { KANBAN_COLUMNS } from '../components/StatusBadge'

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
]

const PAGE_SIZE = 10

export function ProjectDashboard() {
  const { projectSlug } = useParams<{ projectSlug: string }>()
  const navigate = useNavigate()
  const { projects } = useProjects()
  const currentProject = projects.find((p) => p.short_uuid === projectSlug)

  const { tickets, loading, error, statusFilter, setStatusFilter, refresh } = useTickets(
    currentProject?.id
  )
  const [view, setView] = useState<ViewMode>('list')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  if (!projectSlug) return null

  const filteredTickets = useMemo(() => {
    if (!search.trim()) return tickets
    const q = search.toLowerCase()
    return tickets.filter(
      (t) =>
        t.subject.toLowerCase().includes(q) ||
        t.ticket_number.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
    )
  }, [tickets, search])

  const dateSortedTickets = useMemo(() => {
    return [...filteredTickets].sort((a, b) => {
      if (!a.due_date && !b.due_date) return 0
      if (!a.due_date) return 1
      if (!b.due_date) return -1
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
    })
  }, [filteredTickets])

  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pagedTickets = filteredTickets.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const handleStatusChange = (value: string) => {
    setStatusFilter(value || undefined)
    setPage(1)
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={refresh} className="text-sm text-brand-500 hover:text-brand-600 font-medium">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {currentProject?.name || 'Project'} Feedback
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {filteredTickets.length}{filteredTickets.length !== tickets.length ? ` of ${tickets.length}` : ''}{' '}
            {filteredTickets.length === 1 ? 'item' : 'items'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refresh}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <ViewToggle view={view} onChange={setView} />
          <button
            onClick={() => navigate(`/${projectSlug}/new`)}
            className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 text-white rounded-lg font-medium hover:bg-brand-600 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            New Feedback
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-5">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search tickets…"
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none bg-white"
          />
          {search && (
            <button
              onClick={() => handleSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Status filter */}
        <select
          value={statusFilter ?? ''}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {filteredTickets.length === 0 ? (
        search || statusFilter ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            No tickets match your filters.
            <button
              onClick={() => { handleSearch(''); handleStatusChange('') }}
              className="ml-2 text-brand-500 hover:text-brand-600 font-medium"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <EmptyState onNewTicket={() => navigate(`/${projectSlug}/new`)} />
        )
      ) : view === 'list' ? (
        <>
          <div className="space-y-2">
            {pagedTickets.map((ticket) => (
              <TicketCard key={ticket.id} ticket={ticket} projectSlug={projectSlug} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-5 text-sm text-gray-500">
              <span>
                {(safePage - 1) * PAGE_SIZE + 1}–
                {Math.min(safePage * PAGE_SIZE, filteredTickets.length)} of {filteredTickets.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`min-w-[28px] h-7 rounded-md text-xs font-medium transition-colors ${
                      n === safePage
                        ? 'bg-brand-500 text-white'
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : view === 'date' ? (
        <div className="space-y-2">
          {dateSortedTickets.map((ticket) => (
            <TicketCard key={ticket.id} ticket={ticket} projectSlug={projectSlug} />
          ))}
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {KANBAN_COLUMNS.map((col) => (
            <KanbanColumn
              key={col.key}
              label={col.label}
              tickets={filteredTickets.filter((t) => col.statuses.includes(t.status as never))}
              projectSlug={projectSlug}
            />
          ))}
        </div>
      )}
    </div>
  )
}
